import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Calendar, Quote, FileText, Upload, Download, Loader2, Save, Image as ImageIcon, Plus, Trash2, ArrowUp, ArrowDown, Images, Megaphone, Eye, EyeOff } from 'lucide-react';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import logger from '../../utils/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EMPTY_SLIDE = {
  image_url: '',
  alt: '',
};

const DEFAULT_PRESIDENT = {
  enabled: true,
  title: 'Mot du Président du Comité d\'Organisation',
  name: 'SORO Doté',
  function_line_1: "Président du Comité d'Organisation d'URBANIK 2026",
  function_line_2: 'Président de la Confédération des PME/PMI du BTP de Côte d\'Ivoire',
  photo_url: '',
  message: '',
  cta_label: 'Découvrir le forum',
  cta_link: '/evenement#presentation',
};

const DEFAULT_POPUP = {
  enabled: false,
  image_url: '',
  title: 'URBANIK 2026',
  subtitle: "Forum du contenu local dans l'industrie du BTP ivoirien et africain",
  description: 'Du 14 au 16 septembre 2026 au SOFITEL Hôtel Ivoire, Abidjan.',
  primary_label: "S'inscrire",
  primary_link: '/participer',
  secondary_label: 'Télécharger la brochure',
  secondary_link: '/participer#brochure',
  start_date: '',
  end_date: '',
  frequency: 'session', // 'session' | 'daily' | 'always'
};

const generateSlideId = () => `slide-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

export default function ManageSettings() {
  const { token } = useAuth();
  const [settings, setSettings] = useState({});
  const [form, setForm] = useState({ event_date: '', president_name: '', president_role: '', president_quote: '', president_photo: '' });
  const [heroSlides, setHeroSlides] = useState([]);
  const [pressKit, setPressKit] = useState({ available: false, filename: '' });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [slideUploadIdx, setSlideUploadIdx] = useState(null);
  const [mediaDialog, setMediaDialog] = useState({ open: false, slideIdx: null });
  const [galleryImages, setGalleryImages] = useState([]);
  const [popup, setPopup] = useState(DEFAULT_POPUP);
  const [popupUploading, setPopupUploading] = useState(false);
  const [popupPreviewOpen, setPopupPreviewOpen] = useState(false);
  const [president, setPresident] = useState(DEFAULT_PRESIDENT);
  const [presidentUploading, setPresidentUploading] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    const fetch = async () => {
      try {
        const [settingsRes, pkRes] = await Promise.all([
          axios.get(`${API}/settings`),
          axios.get(`${API}/press-kit/status`)
        ]);
        setSettings(settingsRes.data);
        setForm({
          event_date: settingsRes.data.event_date || '',
          president_name: settingsRes.data.president_name || '',
          president_role: settingsRes.data.president_role || '',
          president_quote: settingsRes.data.president_quote || '',
          president_photo: settingsRes.data.president_photo || ''
        });
        const carousel = Array.isArray(settingsRes.data.hero_carousel) ? settingsRes.data.hero_carousel : [];
        // Backfill stable ids for legacy slides without an id
        setHeroSlides(carousel.map(s => (s.id ? s : { ...s, id: generateSlideId() })));
        // Load popup config — merge with defaults so partial saves stay valid
        if (settingsRes.data.popup && typeof settingsRes.data.popup === 'object') {
          setPopup({ ...DEFAULT_POPUP, ...settingsRes.data.popup });
        }
        // Load President section — merge with legacy individual fields + new object
        const presObj = settingsRes.data.president && typeof settingsRes.data.president === 'object' ? settingsRes.data.president : {};
        const legacyPres = {};
        if (settingsRes.data.president_name) legacyPres.name = settingsRes.data.president_name;
        if (settingsRes.data.president_role) legacyPres.function_line_1 = settingsRes.data.president_role;
        if (settingsRes.data.president_quote) legacyPres.message = settingsRes.data.president_quote;
        // Reject legacy Western stock placeholder photo
        if (settingsRes.data.president_photo && !settingsRes.data.president_photo.includes('unsplash.com')) {
          legacyPres.photo_url = settingsRes.data.president_photo;
        }
        setPresident({ ...DEFAULT_PRESIDENT, ...legacyPres, ...presObj });
        setPressKit(pkRes.data);
      } catch (e) { toast.error('Erreur'); }
      finally { setLoading(false); }
    };
    fetch();
    // Intentionally run once on mount. State setters are stable and module
    // constants (API, DEFAULT_POPUP, DEFAULT_PRESIDENT) don't change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await Promise.all([
        ...Object.entries(form).map(([key, value]) =>
          axios.put(`${API}/settings/${key}`, { value }, { headers })
        ),
        axios.put(`${API}/settings/hero_carousel`, { value: heroSlides }, { headers }),
        axios.put(`${API}/settings/popup`, { value: popup }, { headers }),
        axios.put(`${API}/settings/president`, { value: president }, { headers }),
      ]);
      toast.success('Paramètres enregistrés');
    } catch (e) { toast.error('Erreur'); }
    finally { setSaving(false); }
  };

  const handleUploadPressKit = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) { toast.error('Seuls les PDF sont acceptés'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${API}/press-kit/upload`, fd, { headers: { ...headers, 'Content-Type': 'multipart/form-data' } });
      toast.success('Dossier de presse uploadé');
      const pkRes = await axios.get(`${API}/press-kit/status`);
      setPressKit(pkRes.data);
    } catch (e) { toast.error('Erreur upload'); }
    finally { setUploading(false); }
  };

  // ======= Hero Carousel handlers =======
  const addSlide = () => {
    setHeroSlides([...heroSlides, { ...EMPTY_SLIDE, id: generateSlideId() }]);
  };

  const removeSlide = (idx) => {
    setHeroSlides(heroSlides.filter((_, i) => i !== idx));
  };

  const updateSlide = (idx, field, value) => {
    setHeroSlides(heroSlides.map((s, i) => (i === idx ? { ...s, [field]: value } : s)));
  };

  const moveSlide = (idx, direction) => {
    const newIdx = idx + direction;
    if (newIdx < 0 || newIdx >= heroSlides.length) return;
    const copy = [...heroSlides];
    [copy[idx], copy[newIdx]] = [copy[newIdx], copy[idx]];
    setHeroSlides(copy);
  };

  const handleSlideImageUpload = async (e, idx) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSlideUploadIdx(idx);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/upload/image`, fd, { headers: { ...headers, 'Content-Type': 'multipart/form-data' } });
      const url = res.data.url.startsWith('http') ? res.data.url : `${BACKEND_URL}${res.data.url}`;
      updateSlide(idx, 'image_url', url);
      toast.success('Image uploadée');
    } catch (err) { logger.error('Slide image upload failed:', err); toast.error('Erreur upload image'); }
    finally { setSlideUploadIdx(null); }
  };

  const openMediaDialog = async (idx) => {
    setMediaDialog({ open: true, slideIdx: idx });
    try {
      const res = await axios.get(`${API}/gallery/all`, { headers });
      setGalleryImages(res.data);
    } catch (e) { logger.error('Gallery fetch failed:', e); toast.error('Impossible de charger la bibliothèque'); }
  };

  const selectFromGallery = (url) => {
    if (mediaDialog.slideIdx !== null) {
      updateSlide(mediaDialog.slideIdx, 'image_url', url);
    }
    setMediaDialog({ open: false, slideIdx: null });
  };

  // ======= Popup d'accueil handlers =======
  const updatePopup = (field, value) => setPopup(p => ({ ...p, [field]: value }));

  const handlePopupImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPopupUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/upload/image`, fd, { headers: { ...headers, 'Content-Type': 'multipart/form-data' } });
      const url = res.data.url.startsWith('http') ? res.data.url : `${BACKEND_URL}${res.data.url}`;
      updatePopup('image_url', url);
      toast.success('Image popup uploadée');
    } catch (err) {
      logger.error('Popup image upload failed:', err);
      toast.error('Erreur upload image popup');
    } finally {
      setPopupUploading(false);
      e.target.value = '';
    }
  };

  const previewPopup = () => setPopupPreviewOpen(true);

  // ======= Mot du Président handlers =======
  const updatePresident = (field, value) => setPresident(p => ({ ...p, [field]: value }));

  const handlePresidentPhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPresidentUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/upload/image`, fd, { headers: { ...headers, 'Content-Type': 'multipart/form-data' } });
      const url = res.data.url.startsWith('http') ? res.data.url : `${BACKEND_URL}${res.data.url}`;
      updatePresident('photo_url', url);
      toast.success('Photo Président uploadée');
    } catch (err) {
      logger.error('President photo upload failed:', err);
      toast.error('Erreur upload photo');
    } finally {
      setPresidentUploading(false);
      e.target.value = '';
    }
  };

  if (loading) return <div className="flex justify-center py-12"><div className="w-8 h-8 border-3 border-[#1E3A5F] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div data-testid="manage-settings">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-[#1E3A5F]">Paramètres du site</h1>
        <p className="text-sm text-[#6B7C93]">Configuration globale de l'événement</p>
      </div>

      {/* ======= POPUP D'ACCUEIL ======= */}
      <Card className="border-0 shadow-sm mb-6" data-testid="popup-card">
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <CardTitle className="text-lg flex items-center gap-2"><Megaphone className="w-5 h-5 text-[#F5A623]" />Popup d'accueil</CardTitle>
              <p className="text-xs text-[#6B7C93] mt-1">Annonce affichée à l'arrivée sur la page d'accueil. Si désactivée, aucun popup n'est affiché.</p>
            </div>
            <div className="flex items-center gap-3">
              <Button type="button" variant="outline" size="sm" onClick={previewPopup} data-testid="popup-preview-btn">
                <Eye className="w-4 h-4 mr-1" />Prévisualiser
              </Button>
              <label className="flex items-center gap-2 px-3 py-1.5 rounded-md border border-[#E1E8F0] bg-white cursor-pointer">
                <Switch checked={popup.enabled} onCheckedChange={(v) => updatePopup('enabled', v)} data-testid="popup-enabled-switch" />
                <span className="text-sm font-medium text-[#1E3A5F]">{popup.enabled ? 'Actif' : 'Inactif'}</span>
              </label>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-[180px_1fr] gap-4">
            {/* Image */}
            <div>
              <Label className="text-xs">Image</Label>
              <div className="aspect-[16/9] rounded-lg overflow-hidden border border-[#E1E8F0] bg-[#F7F9FC] flex items-center justify-center mb-2">
                {popup.image_url ? (
                  <img src={popup.image_url} alt="" className="w-full h-full object-cover" />
                ) : (
                  <ImageIcon className="w-8 h-8 text-[#D1D5DB]" />
                )}
              </div>
              <label className="flex items-center justify-center gap-1 cursor-pointer px-3 py-1.5 bg-[#1E3A5F] text-white text-xs font-medium rounded-md hover:bg-[#152A45] transition-colors">
                {popupUploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                {popupUploading ? 'Upload...' : 'Choisir une image'}
                <input type="file" accept="image/*" className="hidden" onChange={handlePopupImageUpload} disabled={popupUploading} data-testid="popup-image-upload" />
              </label>
              {popup.image_url && (
                <button type="button" onClick={() => updatePopup('image_url', '')} className="w-full mt-1 text-xs text-red-500 hover:text-red-700">Retirer</button>
              )}
            </div>

            <div className="space-y-3">
              <div>
                <Label className="text-xs">Titre</Label>
                <Input value={popup.title || ''} onChange={(e) => updatePopup('title', e.target.value)} placeholder="URBANIK 2026" data-testid="popup-title-input" />
              </div>
              <div>
                <Label className="text-xs">Sous-titre</Label>
                <Input value={popup.subtitle || ''} onChange={(e) => updatePopup('subtitle', e.target.value)} placeholder="Forum du contenu local..." data-testid="popup-subtitle-input" />
              </div>
              <div>
                <Label className="text-xs">Description courte</Label>
                <Textarea value={popup.description || ''} onChange={(e) => updatePopup('description', e.target.value)} rows={2} placeholder="Du 14 au 16 septembre 2026 au SOFITEL Hôtel Ivoire, Abidjan." data-testid="popup-description-input" />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2 border-t border-[#E1E8F0]">
            <div>
              <Label className="text-xs">Bouton principal — libellé</Label>
              <Input value={popup.primary_label || ''} onChange={(e) => updatePopup('primary_label', e.target.value)} placeholder="S'inscrire" data-testid="popup-primary-label" />
            </div>
            <div>
              <Label className="text-xs">Bouton principal — lien</Label>
              <Input value={popup.primary_link || ''} onChange={(e) => updatePopup('primary_link', e.target.value)} placeholder="/participer" data-testid="popup-primary-link" />
            </div>
            <div>
              <Label className="text-xs">Bouton secondaire — libellé</Label>
              <Input value={popup.secondary_label || ''} onChange={(e) => updatePopup('secondary_label', e.target.value)} placeholder="Télécharger la brochure" data-testid="popup-secondary-label" />
            </div>
            <div>
              <Label className="text-xs">Bouton secondaire — lien</Label>
              <Input value={popup.secondary_link || ''} onChange={(e) => updatePopup('secondary_link', e.target.value)} placeholder="/participer#brochure" data-testid="popup-secondary-link" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-2 border-t border-[#E1E8F0]">
            <div>
              <Label className="text-xs">Date de début (optionnel)</Label>
              <Input type="datetime-local" value={popup.start_date || ''} onChange={(e) => updatePopup('start_date', e.target.value)} data-testid="popup-start-date" />
            </div>
            <div>
              <Label className="text-xs">Date de fin (optionnel)</Label>
              <Input type="datetime-local" value={popup.end_date || ''} onChange={(e) => updatePopup('end_date', e.target.value)} data-testid="popup-end-date" />
            </div>
            <div>
              <Label className="text-xs">Fréquence</Label>
              <Select value={popup.frequency || 'session'} onValueChange={(v) => updatePopup('frequency', v)}>
                <SelectTrigger data-testid="popup-frequency-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="session">Une fois par session</SelectItem>
                  <SelectItem value="daily">Une fois par jour</SelectItem>
                  <SelectItem value="always">À chaque visite</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ======= MOT DU PRÉSIDENT ======= */}
      <Card className="border-0 shadow-sm mb-6" data-testid="mot-president-card">
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <CardTitle className="text-lg flex items-center gap-2"><Quote className="w-5 h-5 text-[#F5A623]" />Mot du Président (Page d'accueil)</CardTitle>
              <p className="text-xs text-[#6B7C93] mt-1">Section institutionnelle affichée sur la page d'accueil. Si désactivée, la section n'apparaît pas sur le site.</p>
            </div>
            <label className="flex items-center gap-2 px-3 py-1.5 rounded-md border border-[#E1E8F0] bg-white cursor-pointer">
              <Switch checked={president.enabled !== false} onCheckedChange={(v) => updatePresident('enabled', v)} data-testid="president-enabled-switch" />
              <span className="text-sm font-medium text-[#1E3A5F]">{president.enabled !== false ? 'Actif' : 'Inactif'}</span>
            </label>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-[180px_1fr] gap-4">
            {/* Photo */}
            <div>
              <Label className="text-xs">Photo du Président</Label>
              <div className="aspect-[4/5] rounded-lg overflow-hidden border border-[#E1E8F0] bg-[#F7F9FC] flex items-center justify-center mb-2">
                {president.photo_url ? (
                  <img src={president.photo_url} alt="" className="w-full h-full object-cover" />
                ) : (
                  <ImageIcon className="w-8 h-8 text-[#D1D5DB]" />
                )}
              </div>
              <label className="flex items-center justify-center gap-1 cursor-pointer px-3 py-1.5 bg-[#1E3A5F] text-white text-xs font-medium rounded-md hover:bg-[#152A45] transition-colors">
                {presidentUploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                {presidentUploading ? 'Upload...' : 'Choisir une photo'}
                <input type="file" accept="image/*" className="hidden" onChange={handlePresidentPhotoUpload} disabled={presidentUploading} data-testid="president-photo-upload" />
              </label>
              {president.photo_url && (
                <button type="button" onClick={() => updatePresident('photo_url', '')} className="w-full mt-1 text-xs text-red-500 hover:text-red-700">Retirer</button>
              )}
            </div>

            <div className="space-y-3">
              <div>
                <Label className="text-xs">Titre de la section</Label>
                <Input value={president.title || ''} onChange={(e) => updatePresident('title', e.target.value)} data-testid="president-title-input" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Nom</Label>
                  <Input value={president.name || ''} onChange={(e) => updatePresident('name', e.target.value)} data-testid="president-name-input" />
                </div>
                <div>
                  <Label className="text-xs">Fonction principale</Label>
                  <Input value={president.function_line_1 || ''} onChange={(e) => updatePresident('function_line_1', e.target.value)} />
                </div>
              </div>
              <div>
                <Label className="text-xs">Fonction secondaire (optionnel)</Label>
                <Input value={president.function_line_2 || ''} onChange={(e) => updatePresident('function_line_2', e.target.value)} />
              </div>
              <div>
                <Label className="text-xs">Message du Président</Label>
                <Textarea value={president.message || ''} onChange={(e) => updatePresident('message', e.target.value)} rows={8} className="text-sm" data-testid="president-message-input" placeholder="Texte intégral du message..." />
                <p className="text-[11px] text-[#94A3B8] mt-1">Séparez les paragraphes par une ligne vide. Au-delà de 2 paragraphes, un bouton "Lire la suite" apparaît automatiquement sur le site.</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2 border-t border-[#E1E8F0]">
                <div>
                  <Label className="text-xs">Bouton CTA — libellé</Label>
                  <Input value={president.cta_label || ''} onChange={(e) => updatePresident('cta_label', e.target.value)} />
                </div>
                <div>
                  <Label className="text-xs">Bouton CTA — lien</Label>
                  <Input value={president.cta_link || ''} onChange={(e) => updatePresident('cta_link', e.target.value)} placeholder="/evenement#presentation" />
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ======= HERO CAROUSEL ======= */}
      <Card className="border-0 shadow-sm mb-6" data-testid="hero-carousel-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2"><Images className="w-5 h-5 text-[#F5A623]" />Carousel Hero (Page d'accueil)</CardTitle>
            <Button onClick={addSlide} size="sm" variant="outline" data-testid="add-slide-btn"><Plus className="w-4 h-4 mr-1" />Ajouter une image</Button>
          </div>
          <p className="text-xs text-[#6B7C93] mt-1">Uploadez, supprimez et réordonnez les images du carousel affiché en haut de la page d'accueil. Format recommandé : 1920×1080 px (16:9). Si aucune image n'est ajoutée, une image par défaut est utilisée.</p>
        </CardHeader>
        <CardContent>
          {heroSlides.length === 0 ? (
            <div className="text-center py-8 border-2 border-dashed border-[#E1E8F0] rounded-lg">
              <Images className="w-10 h-10 text-[#D1D5DB] mx-auto mb-2" />
              <p className="text-sm text-[#6B7C93]">Aucune image configurée</p>
              <p className="text-xs text-[#94A3B8] mt-1">L'image par défaut sera affichée sur le site</p>
            </div>
          ) : (
            <div className="space-y-3">
              {heroSlides.map((slide, idx) => (
                <div key={slide.id || idx} className="border border-[#E1E8F0] rounded-lg p-3 bg-[#F7F9FC] flex items-center gap-4" data-testid={`hero-slide-editor-${idx}`}>
                  {/* Image preview large */}
                  <div className="w-48 h-28 rounded-lg overflow-hidden border border-[#E1E8F0] bg-white flex-shrink-0 flex items-center justify-center">
                    {slide.image_url ? (
                      <img src={slide.image_url} alt={slide.alt || ''} className="w-full h-full object-cover" />
                    ) : (
                      <ImageIcon className="w-8 h-8 text-[#D1D5DB]" />
                    )}
                  </div>

                  <div className="flex-1 min-w-0 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-[#1E3A5F] uppercase">Image #{idx + 1}</span>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => moveSlide(idx, -1)} disabled={idx === 0} title="Monter" data-testid={`slide-up-${idx}`}><ArrowUp className="w-3.5 h-3.5" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => moveSlide(idx, 1)} disabled={idx === heroSlides.length - 1} title="Descendre" data-testid={`slide-down-${idx}`}><ArrowDown className="w-3.5 h-3.5" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => removeSlide(idx)} className="text-red-500 hover:text-red-700 hover:bg-red-50" title="Supprimer" data-testid={`slide-delete-${idx}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                      </div>
                    </div>

                    {/* Image URL + upload + gallery */}
                    <div>
                      <Label className="text-xs">URL ou upload</Label>
                      <div className="flex gap-2">
                        <Input value={slide.image_url} onChange={(e) => updateSlide(idx, 'image_url', e.target.value)} placeholder="https://... ou cliquez sur l'icône d'upload" className="text-sm" data-testid={`slide-url-${idx}`} />
                        <label className="flex-shrink-0 flex items-center gap-1 cursor-pointer px-3 py-2 bg-[#1E3A5F] text-white text-xs font-medium rounded-md hover:bg-[#152A45] transition-colors" title="Uploader depuis votre ordinateur">
                          {slideUploadIdx === idx ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                          <input type="file" accept="image/*" className="hidden" onChange={(e) => handleSlideImageUpload(e, idx)} disabled={slideUploadIdx === idx} data-testid={`slide-upload-${idx}`} />
                        </label>
                        <Button type="button" variant="outline" size="sm" onClick={() => openMediaDialog(idx)} title="Bibliothèque médias" data-testid={`slide-gallery-${idx}`}><ImageIcon className="w-3.5 h-3.5" /></Button>
                      </div>
                    </div>

                    <div>
                      <Label className="text-xs">Texte alternatif (accessibilité / SEO)</Label>
                      <Input value={slide.alt || ''} onChange={(e) => updateSlide(idx, 'alt', e.target.value)} placeholder="Décrivez brièvement l'image" className="text-sm" data-testid={`slide-alt-${idx}`} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-0 shadow-sm">
          <CardHeader><CardTitle className="text-lg flex items-center gap-2"><Calendar className="w-5 h-5 text-[#F5A623]" />Date de l'événement</CardTitle></CardHeader>
          <CardContent>
            <div><Label>Date et heure (ISO)</Label><Input value={form.event_date} onChange={(e) => setForm({ ...form, event_date: e.target.value })} placeholder="2026-09-14T09:00:00" data-testid="settings-event-date" /></div>
            <p className="text-xs text-[#6B7C93] mt-1">Format: AAAA-MM-JJTHH:MM:SS</p>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardHeader><CardTitle className="text-lg flex items-center gap-2"><Quote className="w-5 h-5 text-[#F5A623]" />Citation du Président</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div><Label>Nom</Label><Input value={form.president_name} onChange={(e) => setForm({ ...form, president_name: e.target.value })} data-testid="settings-president-name" /></div>
            <div><Label>Fonction</Label><Input value={form.president_role} onChange={(e) => setForm({ ...form, president_role: e.target.value })} /></div>
            <div><Label>Citation</Label><Textarea value={form.president_quote} onChange={(e) => setForm({ ...form, president_quote: e.target.value })} rows={3} /></div>
            <div><Label>URL Photo</Label><Input value={form.president_photo} onChange={(e) => setForm({ ...form, president_photo: e.target.value })} /></div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardHeader><CardTitle className="text-lg flex items-center gap-2"><FileText className="w-5 h-5 text-[#F5A623]" />Dossier de Presse</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {pressKit.available ? (
              <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg border border-green-200">
                <FileText className="w-5 h-5 text-green-600 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-green-800 truncate">{pressKit.filename}</p>
                  <p className="text-xs text-green-600">Disponible en téléchargement</p>
                </div>
                <a href={`${API}/press-kit/download`} target="_blank" rel="noopener noreferrer" className="text-green-600 hover:text-green-800"><Download className="w-4 h-4" /></a>
              </div>
            ) : (
              <div className="flex items-center gap-3 p-3 bg-[#F0F2F5] rounded-lg"><FileText className="w-5 h-5 text-[#94A3B8]" /><p className="text-sm text-[#6B7C93]">Aucun fichier</p></div>
            )}
            <label className="flex items-center gap-2 cursor-pointer px-4 py-2.5 bg-[#1E3A5F] text-white text-sm font-medium rounded-lg hover:bg-[#152A45] transition-colors w-fit">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {uploading ? 'Upload...' : 'Choisir un PDF'}
              <input type="file" accept=".pdf" className="hidden" onChange={handleUploadPressKit} disabled={uploading} data-testid="press-kit-upload" />
            </label>
          </CardContent>
        </Card>
      </div>

      <div className="mt-6">
        <Button onClick={handleSave} disabled={saving} className="bg-[#F5A623] hover:bg-[#E09615] text-[#1E3A5F] font-semibold px-8" data-testid="save-settings-btn">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Enregistrer les paramètres
        </Button>
      </div>

      {/* Media Library Dialog */}
      <Dialog open={mediaDialog.open} onOpenChange={(open) => !open && setMediaDialog({ open: false, slideIdx: null })}>
        <DialogContent className="bg-white max-w-3xl max-h-[70vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Sélectionner une image de la bibliothèque</DialogTitle></DialogHeader>
          <div className="grid grid-cols-4 gap-2 mt-2">
            {galleryImages.map(img => (
              <button key={img.id} onClick={() => selectFromGallery(`${BACKEND_URL}${img.url}`)} className="rounded-lg overflow-hidden border-2 border-transparent hover:border-[#F5A623]" data-testid={`gallery-select-${img.id}`}>
                <img src={`${BACKEND_URL}${img.url}`} alt={img.title || ''} className="w-full h-20 object-cover" />
              </button>
            ))}
            {galleryImages.length === 0 && <p className="col-span-4 text-center py-8 text-[#94A3B8]">Aucune image dans la bibliothèque</p>}
          </div>
        </DialogContent>
      </Dialog>

      {/* ======= POPUP PREVIEW ======= */}
      <Dialog open={popupPreviewOpen} onOpenChange={setPopupPreviewOpen}>
        <DialogContent className="bg-white max-w-lg p-0 overflow-hidden">
          <DialogHeader className="px-5 py-3 border-b border-[#E1E8F0]">
            <DialogTitle className="text-sm flex items-center gap-2"><EyeOff className="w-4 h-4" />Aperçu de la popup</DialogTitle>
          </DialogHeader>
          <div className="bg-white">
            {popup.image_url && (
              <div className="w-full aspect-[16/9] bg-[#1E3A5F]">
                <img src={popup.image_url} alt="" className="w-full h-full object-cover" />
              </div>
            )}
            <div className="px-6 py-6">
              {popup.title && <h2 className="text-2xl font-bold text-[#1E3A5F]">{popup.title}</h2>}
              {popup.subtitle && <p className="mt-2 text-sm text-[#F5A623] font-semibold">{popup.subtitle}</p>}
              {popup.description && <p className="mt-4 text-sm text-[#4A5568] leading-relaxed">{popup.description}</p>}
              {(popup.primary_label || popup.secondary_label) && (
                <div className="mt-6 flex flex-col sm:flex-row gap-3">
                  {popup.primary_label && (
                    <span className="inline-flex items-center justify-center px-5 py-2.5 rounded-full bg-[#F5A623] text-[#1E3A5F] text-sm font-semibold">{popup.primary_label}</span>
                  )}
                  {popup.secondary_label && (
                    <span className="inline-flex items-center justify-center px-5 py-2.5 rounded-full border border-[#E1E8F0] text-[#1E3A5F] text-sm font-semibold">{popup.secondary_label}</span>
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="px-5 py-3 bg-[#F7F9FC] border-t border-[#E1E8F0] text-xs text-[#6B7C93]">
            Aperçu rendu avec la configuration actuelle. Pensez à enregistrer pour appliquer en ligne.
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
